// Checkout extras (docs/CHECKOUT_API_2026-10-28.md §10, docs/DINING_API_2026-10-28.md §12):
//   ReceiptActions  print / text / e-mail a paid check or register / kiosk order
//   RefundBody      void or refund a payment - shows "refunded" only once Aster Gateway says so
//   RefundModal     RefundBody in a dining modal (an approval code when the owner switched refunds on)
//   PartCardModal   charge part of a check on the card, with its own tip
//   (split payment, one payment at a time: SplitPayFlow.jsx)
// Every result on these screens is the server's answer: a print is "queued" until
// the printer reports, a text is "sent" only when the SMS service accepted it,
// a refund is "approved" only on the gateway's final word.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorText, newKey } from '../lib/api';
import { money } from '../lib/format';
import { C, FONT_TIGHT } from '../theme/tokens';
import { Btn, ErrorNote, Modal, ModalFoot, ModalHead, Segmented } from './ui';
import { useApproval } from './ApprovalDialog';
import {
  IconBackspace, IconCard, IconCheck, IconChat, IconClose, IconReceipt, IconSend, IconSpinner, IconUndo,
} from './icons';

const POLL_MS = 2000;

function palette(dark) {
  return dark
    ? { surface: '#0F172A', card: '#1E293B', text: '#F1F5F9', muted: '#94A3B8', border: '#334155', input: '#0F172A' }
    : { surface: C.cream, card: '#FFFFFF', text: C.ink, muted: C.muted, border: C.border, input: '#FAF8F5' };
}

function Status({ tone, children, busy }) {
  const tones = {
    ok: { bg: C.greenSoft, fg: '#14532D' },
    wait: { bg: C.amberSoft, fg: C.amberInk },
    bad: { bg: C.redSoft, fg: C.redInk },
    info: { bg: C.sand, fg: C.ink2 },
  };
  const t = tones[tone] || tones.info;
  return (
    <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, borderRadius: 12, background: t.bg, color: t.fg, padding: '9px 12px', fontSize: 13.5, fontWeight: 650, lineHeight: 1.4 }}>
      <span style={{ flex: 'none', display: 'flex' }}>{busy ? <IconSpinner size={16} /> : tone === 'ok' ? <IconCheck size={15} /> : tone === 'bad' ? <IconClose size={15} /> : null}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </div>
  );
}

function detailText(e) {
  const d = e?.detail;
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  return errorText(e);
}

// ------------------------------------------------------------------ receipts

/**
 * kind: "check" | "order"; id: the check id / order id.
 * dark: on the register's dark dialog. Inline (no modal) so it also works inside other dialogs.
 */
export function ReceiptActions({ kind, id, dark = false, title = 'Receipt', defaultPhone = '' }) {
  const p = palette(dark);
  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState(null);
  const [mode, setMode] = useState(null); // null | 'text' | 'email'
  const [phone, setPhone] = useState(defaultPhone || '');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState({}); // channel -> {tone, text, busy}
  const jobTimer = useRef(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    setInfo(null); setInfoError(null); setResult({}); setMode(null);
    api.get(`/checkout/receipts/${kind}/${id}`).then((r) => { if (alive.current) setInfo(r); })
      .catch((e) => { if (alive.current) setInfoError(e); });
    return () => { alive.current = false; clearTimeout(jobTimer.current); };
  }, [kind, id]);

  const show = (channel, value) => setResult((r) => ({ ...r, [channel]: value }));

  const watchJob = useCallback((jobId, startedAt) => {
    clearTimeout(jobTimer.current);
    jobTimer.current = setTimeout(async () => {
      try {
        const job = await api.get(`/checkout/receipt-print-jobs/${jobId}`);
        if (!alive.current) return;
        show('print', { tone: job.printed ? 'ok' : job.is_final ? 'bad' : 'wait', text: job.message, busy: !job.is_final });
        if (!job.is_final && Date.now() - startedAt < 120000) watchJob(jobId, startedAt);
        else if (!job.is_final) show('print', { tone: 'wait', text: `${job.message} - the printer has not reported yet`, busy: false });
      } catch (e) {
        if (alive.current && Date.now() - startedAt < 120000) watchJob(jobId, startedAt);
      }
    }, POLL_MS);
  }, []);

  const doPrint = async () => {
    setBusy('print');
    show('print', null);
    try {
      const job = await api.post(`/checkout/receipts/${kind}/${id}/print`, {}, { idempotencyKey: newKey('print') });
      show('print', { tone: job.printed ? 'ok' : 'wait', text: job.message, busy: !job.is_final });
      if (!job.is_final) watchJob(job.job_id, Date.now());
    } catch (e) {
      show('print', { tone: 'bad', text: detailText(e) });
    } finally {
      setBusy(null);
    }
  };

  const send = async (channel) => {
    setBusy(channel);
    show(channel, null);
    try {
      const body = channel === 'text' ? { phone } : { email };
      const r = await api.post(`/checkout/receipts/${kind}/${id}/${channel === 'text' ? 'text' : 'email'}`, body,
        { idempotencyKey: newKey(channel) });
      if (r.status === 'sending') show(channel, { tone: 'wait', text: 'Still sending - this was already tapped.' });
      else show(channel, { tone: r.sent ? 'ok' : 'bad', text: r.message });
      if (r.sent) setMode(null);
    } catch (e) {
      show(channel, { tone: 'bad', text: detailText(e) });
    } finally {
      setBusy(null);
    }
  };

  const tile = (on) => ({
    flex: 1, minWidth: 0, height: 52, borderRadius: 14, border: on ? `2px solid ${C.orange}` : `1px solid ${p.border}`,
    background: on ? (dark ? 'rgba(249,115,22,.12)' : C.orangeTint) : p.card, color: p.text, fontSize: 14.5, fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  });
  const input = { height: 50, borderRadius: 12, border: `1px solid ${p.border}`, padding: '0 14px', fontSize: 17, color: p.text, background: p.input, width: '100%', minWidth: 0 };
  const unpaid = info && !info.paid;

  return (
    <div data-testid="receipt-actions" style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: p.muted }}>{title}</span>
        {info && <span style={{ fontSize: 12.5, color: p.muted }}>{money(info.charged_cents)}{info.refunded_cents > 0 ? ` · ${money(info.refunded_cents)} refunded` : ''}</span>}
      </div>
      {infoError && <ErrorNote light={!dark} compact error={infoError} />}
      {unpaid && <Status tone="info">A receipt is sent once this is paid.</Status>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="c29-press" style={tile(false)} disabled={!info || unpaid || busy === 'print'} onClick={doPrint} data-testid="receipt-print">
          {busy === 'print' ? <IconSpinner size={18} /> : <IconReceipt size={18} />}Print
        </button>
        <button type="button" className="c29-press" style={tile(mode === 'text')} disabled={!info || unpaid} onClick={() => setMode(mode === 'text' ? null : 'text')} data-testid="receipt-text">
          <IconChat size={18} />Text
        </button>
        {info?.can_email && (
          <button type="button" className="c29-press" style={tile(mode === 'email')} disabled={unpaid} onClick={() => setMode(mode === 'email' ? null : 'email')} data-testid="receipt-email">
            <IconSend size={17} />E-mail
          </button>
        )}
      </div>
      {info && !info.can_print && !result.print && (
        <span style={{ fontSize: 12.5, color: p.muted, lineHeight: 1.45 }}>No receipt printer is chosen yet - the owner picks one in Payment settings.</span>
      )}
      {mode === 'text' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input aria-label="Guest mobile number" inputMode="tel" autoComplete="off" placeholder="Guest mobile number" value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/[^0-9+()\- ]/g, '').slice(0, 20))}
            onKeyDown={(e) => { if (e.key === 'Enter' && phone.replace(/\D/g, '').length >= 10) send('text'); }} style={input} />
          <Btn kind="ink" height={50} busy={busy === 'text'} disabled={phone.replace(/\D/g, '').length < 10} onClick={() => send('text')}>Send</Btn>
        </div>
      )}
      {mode === 'email' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input aria-label="Guest e-mail" type="email" autoComplete="off" placeholder="guest@example.com" value={email}
            onChange={(e) => setEmail(e.target.value.slice(0, 254))} style={input} />
          <Btn kind="ink" height={50} busy={busy === 'email'} disabled={!/^\S+@\S+\.\S{2,}$/.test(email)} onClick={() => send('email')}>Send</Btn>
        </div>
      )}
      {['print', 'text', 'email'].map((ch) => result[ch] && <Status key={ch} tone={result[ch].tone} busy={result[ch].busy}>{result[ch].text}</Status>)}
    </div>
  );
}

// ------------------------------------------------------------------ amount keypad

function AmountPad({ cents, onChange, max, dark }) {
  const p = palette(dark);
  const press = (k) => {
    const s = String(cents || '');
    if (k === 'del') onChange(Number(s.slice(0, -1)) || 0);
    else onChange(Math.min(Number(`${s}${k}`.replace(/^0+/, '').slice(0, 8)) || 0, max ?? Infinity));
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
      {['1', '2', '3', '4', '5', '6', '7', '8', '9', '00', '0', 'del'].map((k) => (
        <button key={k} type="button" className="c29-press c29-tight" onClick={() => press(k)} aria-label={k === 'del' ? 'Delete' : k}
          style={{ height: 54, borderRadius: 14, border: `1px solid ${p.border}`, background: p.card, color: p.text, fontSize: 22, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {k === 'del' ? <IconBackspace size={22} /> : k}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ refunds / voids

const REASONS = [
  { value: 'customer_request', label: 'Guest asked' },
  { value: 'quality_issue', label: 'Quality' },
  { value: 'order_error', label: 'Wrong order' },
  { value: 'duplicate', label: 'Charged twice' },
  { value: 'other', label: 'Other' },
];

function entriesOf(summary, scope) {
  if (!summary) return [];
  // a check, or a register order paid with several tenders (split payment): every card on its own
  if (scope.kind === 'check' || summary.split) {
    const cards = (summary.cards || []).map((c) => ({
      key: `card-${c.payment_id}`, kind: 'card', label: `${c.card_brand || 'Card'}${c.last4 ? ` •••• ${c.last4}` : ''}`, ...c,
    }));
    const cash = summary.cash && summary.cash.captured_cents > 0
      ? [{ key: 'cash', kind: 'cash', label: 'Cash / by hand', can_void: false, reversals: [], pending_cents: 0, ...summary.cash }] : [];
    return [...cards, ...cash];
  }
  const card = ['card', 'online_card'].includes(summary.tender);
  return [{ key: 'order', kind: card ? 'card' : 'cash', label: card ? 'Card payment' : (summary.tender ? summary.tender.replace('_', ' ') : 'Payment'), ...summary }];
}

/**
 * scope: {kind: 'check', id, checkNo, sessionId} (the check doors)
 *     or {kind: 'order', id} (the register order doors).
 * Who may refund / void is the role (owner, manager, supervisor, cashier); when the owner
 * switched Refund / Void on (Approval codes), a non-approver is asked for an owner's /
 * manager's approval code: the server answers approval_required and the dialog opens.
 * run: a screen's useApproval().run; omitted = this body's own.
 */
export function RefundBody({ scope, run: runFromScreen, onChanged, dark = false, onDone }) {
  const p = palette(dark);
  const own = useApproval({ inline: dark });
  const run = runFromScreen || own.run;
  const base = scope.kind === 'check' ? `/dining/checks/${scope.id}` : `/merchant/orders/${scope.id}`;
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [pick, setPick] = useState(null); // {entry, action: 'refund'|'void'}
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('customer_request');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [outcome, setOutcome] = useState(null); // {tone, text, busy}
  const timer = useRef(null);
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const s = await api.get(`${base}/refunds`);
      if (!alive.current) return null;
      setSummary(s);
      setLoadError(null);
      return s;
    } catch (e) {
      if (alive.current) setLoadError(e);
      return null;
    }
  }, [base]);

  const watch = useCallback((reversalId) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`${base}/reversals/${reversalId}`);
        if (!alive.current) return;
        if (!r.is_final) { setOutcome({ tone: 'wait', busy: true, text: `${r.kind === 'void' ? 'Void' : 'Refund'} of ${money(r.amount_cents)} sent - waiting for Aster Gateway` }); watch(reversalId); return; }
        setOutcome(r.status === 'approved'
          ? { tone: 'ok', text: `Aster Gateway approved the ${r.kind} of ${money(r.amount_cents)}` }
          : { tone: 'bad', text: `Not ${r.kind === 'void' ? 'voided' : 'refunded'}: ${r.message || r.status}. Nothing changed on the books.` });
        await load();
        onChanged?.();
      } catch (e) {
        if (alive.current) watch(reversalId); // a dropped connection never decides a refund: keep asking
      }
    }, POLL_MS);
  }, [base, load, onChanged]);

  useEffect(() => {
    alive.current = true;
    load().then((s) => {
      // a void / refund still waiting from before: keep reading it
      const open = entriesOf(s, scope).flatMap((e) => e.reversals || []).find((r) => !r.is_final);
      if (open) { setOutcome({ tone: 'wait', busy: true, text: `${open.kind === 'void' ? 'Void' : 'Refund'} of ${money(open.amount_cents)} - waiting for Aster Gateway` }); watch(open.payment_id); }
    });
    return () => { alive.current = false; clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const choose = (entry, action) => {
    setPick({ entry, action });
    setAmount(action === 'void' ? entry.captured_cents : entry.refundable_cents);
    setReason('customer_request');
    setError(null);
  };

  const submit = async () => {
    const { entry, action } = pick;
    setBusy(true);
    setError(null);
    const body = { reason, notes: null };
    if (scope.kind === 'check' || summary?.split) {
      if (entry.kind === 'card') body.payment_id = entry.payment_id;
      if (action === 'refund') Object.assign(body, { tender: entry.kind === 'card' ? 'card' : 'cash', amount_cents: amount, refunded_cents_before: entry.refunded_cents_before });
    } else if (action === 'refund') {
      Object.assign(body, { amount_cents: amount, refunded_cents_before: entry.refunded_cents_before });
    }
    const path = `${base}/${action === 'void' ? (scope.kind === 'check' ? 'void-payment' : 'void') : 'refund'}`;
    const call = (token) => api.post(path, token ? { ...body, approval_token: token } : body);
    try {
      const answer = await run(call, { action: action === 'void' ? 'payments.void' : 'payments.refund', entityId: scope.id, sessionId: scope.sessionId, what: scope.checkNo ? `Check ${scope.checkNo}` : `Order #${scope.id}`, reason });
      if (answer === null) { setError(`Approval cancelled - nothing was ${action === 'void' ? 'voided' : 'refunded'}.`); return; }
      setPick(null);
      if (answer.is_final && answer.status === 'approved') {
        setOutcome({ tone: 'ok', text: answer.tender && answer.message ? answer.message : `Aster Gateway approved the ${answer.kind} of ${money(answer.amount_cents)}` });
        await load();
        onChanged?.();
      } else {
        setOutcome({ tone: 'wait', busy: true, text: `${answer.kind === 'void' ? 'Void' : 'Refund'} of ${money(answer.amount_cents)} sent - waiting for Aster Gateway` });
        watch(answer.payment_id);
        onChanged?.();
      }
    } catch (e) {
      const reversal = e?.body?.reversal;
      if (reversal) {
        setPick(null);
        setOutcome({ tone: 'bad', text: `Not ${reversal.kind === 'void' ? 'voided' : 'refunded'}: ${reversal.message}. Nothing changed on the books.` });
        load();
      } else {
        setError(detailText(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const entries = entriesOf(summary, scope);
  const waiting = outcome?.busy;
  const row = (label, value, color) => <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: p.muted }}><span>{label}</span><span style={{ color: color || p.text, fontWeight: 650 }}>{value}</span></div>;

  // the register (dark, inside its own dialog): the approval takes the panel's place
  if (!runFromScreen && dark && own.asking) return own.dialog;

  if (pick) {
    const { entry, action } = pick;
    const max = action === 'void' ? entry.captured_cents : entry.refundable_cents;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="refund-form">
        {!runFromScreen && !dark && own.dialog}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: p.muted }}>{action === 'void' ? 'Void' : 'Refund'} · {entry.label}</span>
          <span className="c29-tight" style={{ fontFamily: FONT_TIGHT, fontSize: 44, fontWeight: 800, letterSpacing: '-1px', color: p.text, lineHeight: 1.05 }}>{money(amount)}</span>
          <span style={{ fontSize: 13, color: p.muted }}>
            {action === 'void' ? 'The whole payment goes back; the check is owed again.' : `Up to ${money(max)} can be refunded${amount < max ? ` · ${money(max - amount)} stays paid` : ''}`}
          </span>
        </div>
        {action === 'refund' && (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['All', max], ['Half', Math.round(max / 2)]].map(([label, v]) => (
                <Btn key={label} kind="white" height={44} style={{ flex: 1 }} onClick={() => setAmount(v)}>{label} · {money(v)}</Btn>
              ))}
            </div>
            <AmountPad cents={amount} onChange={setAmount} max={max} dark={dark} />
          </>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {REASONS.map((r) => (
            <button key={r.value} type="button" aria-pressed={reason === r.value} onClick={() => setReason(r.value)} className="c29-press"
              style={{ height: 40, padding: '0 12px', borderRadius: 10, border: reason === r.value ? `2px solid ${C.ink}` : `1px solid ${p.border}`, background: reason === r.value ? C.ink : p.card, color: reason === r.value ? '#FFFFFF' : p.text, fontSize: 13.5, fontWeight: 700 }}>{r.label}</button>
          ))}
        </div>
        {entry.kind === 'cash' && action === 'refund' && <span style={{ fontSize: 12.5, color: p.muted }}>Cash and hand-taken payments are given back by hand; this records it on the books.</span>}
        {error && <ErrorNote light={!dark} compact error={error} />}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 8 }}>
          <Btn kind="white" height={52} onClick={() => setPick(null)}>Back</Btn>
          <Btn kind="ink" height={52} busy={busy} disabled={!(amount > 0) || amount > max} onClick={submit} data-testid="refund-submit">
            {action === 'void' ? `Void ${money(amount)}` : `Refund ${money(amount)}`}
          </Btn>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="refund-body">
      {!runFromScreen && !dark && own.dialog}
      {loadError && <ErrorNote light={!dark} compact error={loadError} onRetry={load} />}
      {!summary && !loadError && <Status tone="info" busy>Reading what was paid…</Status>}
      {outcome && <Status tone={outcome.tone} busy={outcome.busy}>{outcome.text}</Status>}
      {summary && !entries.length && <Status tone="info">Nothing was paid here that can be given back.</Status>}
      {entries.map((e) => (
        <div key={e.key} style={{ borderRadius: 16, border: `1px solid ${p.border}`, background: p.card, padding: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: p.muted, display: 'flex' }}>{e.kind === 'card' ? <IconCard size={18} /> : <IconUndo size={18} />}</span>
            <span style={{ flex: 1, fontSize: 15, fontWeight: 800, color: p.text, textTransform: e.kind === 'cash' ? 'none' : 'none' }}>{e.label}</span>
            {e.voided && <span style={{ fontSize: 12, fontWeight: 800, color: C.redInk, background: C.redSoft, borderRadius: 8, padding: '3px 8px' }}>Voided</span>}
          </div>
          {row('Paid', money(e.captured_cents))}
          {e.refunded_cents > 0 && row('Refunded', `−${money(e.refunded_cents).replace('−', '')}`, C.redInk)}
          {e.pending_cents > 0 && row('Waiting for Aster Gateway', money(e.pending_cents), C.amberInk)}
          {(e.reversals || []).filter((r) => r.is_final && r.status !== 'approved').slice(-1).map((r) => (
            <span key={r.payment_id} style={{ fontSize: 12.5, color: C.redInk }}>Last {r.kind} not done: {r.message}</span>
          ))}
          {e.unavailable_reason && <span style={{ fontSize: 12.5, color: p.muted }}>{e.unavailable_reason}</span>}
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            {e.can_void && <Btn kind="white" height={46} style={{ flex: 1 }} disabled={waiting} onClick={() => choose(e, 'void')} data-testid="refund-void">Void</Btn>}
            {e.can_refund && <Btn kind="ink" height={46} style={{ flex: 1 }} disabled={waiting} onClick={() => choose(e, 'refund')} data-testid="refund-start">Refund · up to {money(e.refundable_cents)}</Btn>}
          </div>
        </div>
      ))}
      {onDone && <Btn kind="white" full height={50} onClick={onDone}>{waiting ? 'Close - it keeps going' : 'Done'}</Btn>}
    </div>
  );
}

export function RefundModal({ open, check, sessionId, run, onClose, onChanged }) {
  if (!open || !check) return null;
  return (
    <Modal open label="Refund or void" onClose={onClose} width={560}>
      <ModalHead title="Refund or void" sub={`Check ${check.check_no} · ${money(check.paid_cents)} paid`} onClose={onClose} />
      <div className="c29-scroll" style={{ padding: '16px 24px 20px' }}>
        <RefundBody scope={{ kind: 'check', id: check.id, checkNo: check.check_no, sessionId }} run={run} onChanged={onChanged} />
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------ part of a check on the card

export function PartCardModal({ open, due, total, presets = [], tipOnTerminal, terminalLabel, onClose, onPay }) {
  const [amount, setAmount] = useState(0);
  const [tipPct, setTipPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setAmount(Math.round(due / 2)); setTipPct(0); setError(null); } }, [open, due]);
  if (!open) return null;
  const tip = Math.round((amount || 0) * tipPct / 100);
  const valid = amount > 0 && amount <= due;
  const pay = async () => {
    setBusy(true);
    setError(null);
    try {
      await onPay(amount, tip);
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const quick = [['Half', Math.round(due / 2)], ['A third', Math.round(due / 3)], ['$20', Math.min(2000, due)], ['All', due]];
  return (
    <Modal open label="Card for part of the check" onClose={onClose} width={560}>
      <ModalHead title="Card · part of the check" sub={`${money(due)} due of ${money(total)} · the rest stays open for another card or cash`} onClose={onClose} />
      <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ borderRadius: 18, background: C.ink, color: '#FFFFFF', padding: '14px 18px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#FDBA74' }}>Pay {money(amount)} of {money(due)}</span>
            <span className="c29-tight" style={{ fontFamily: FONT_TIGHT, fontSize: 46, fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.05 }} data-testid="part-amount">{money(amount + tip)}</span>
          </span>
          <span style={{ fontSize: 13, color: '#D6D3D1', textAlign: 'right' }}>{tip ? `incl. ${money(tip)} tip` : tipOnTerminal ? 'tip on the terminal' : 'no tip'}<br />{valid ? `${money(due - amount)} left after` : amount > due ? 'more than is due' : ''}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
          {quick.map(([label, v]) => <Btn key={label} kind="white" height={44} onClick={() => setAmount(v)} style={{ padding: '0 8px' }}>{label}</Btn>)}
        </div>
        <AmountPad cents={amount} onChange={setAmount} max={due} />
        {presets.length > 0 && !tipOnTerminal && (
          <Segmented value={tipPct} onChange={setTipPct} options={[{ value: 0, label: 'No tip' }, ...presets.map((pct) => ({ value: pct, label: `${pct}% · ${money(Math.round((amount || 0) * pct / 100))}` }))]} />
        )}
        {error && <ErrorNote light compact error={error} />}
      </div>
      <ModalFoot>
        <Btn kind="white" height={56} onClick={onClose}>Cancel</Btn>
        <Btn kind="orange" height={56} busy={busy} disabled={!valid} onClick={pay} icon={<IconCard size={20} />} data-testid="part-pay">Send {money(amount + tip)} to {terminalLabel || 'the terminal'}</Btn>
      </ModalFoot>
    </Modal>
  );
}
